import React from 'react';
import styled from 'styled-components';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

// Sort direction indicator for table headers.
// Active sort renders at full opacity; a sortable-but-unsorted column renders a
// ghosted double-chevron that brightens on header hover, so the affordance is
// there without cluttering every column.

interface SortIndicatorProps {
  column: {
    isSorted: boolean;
    isSortedDesc?: boolean;
    canSort: boolean;
  };
}

// Lays out the header label and its sort icon as a flex row. Without this the
// icon sits in the label's text flow, so a header that wraps to two lines
// pushes its icon onto the second line and the row's icons stop lining up.
export const HeaderContent = styled.span<{ $alignRight?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$alignRight ? 'flex-end' : 'flex-start')};
  gap: 4px;
`;

const Wrapper = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${(p) => (p.$active ? p.theme.colors.danger : 'inherit')};
  opacity: ${(p) => (p.$active ? 1 : 0.5)};
  transition:
    opacity 0.15s ease,
    color 0.15s ease;

  th:hover & {
    opacity: ${(p) => (p.$active ? 1 : 0.8)};
  }
`;

const SortIndicator = ({ column }: SortIndicatorProps) => {
  const { isSorted, isSortedDesc, canSort } = column;

  if (!canSort) return null;

  return (
    <Wrapper $active={isSorted} aria-hidden="true">
      {!isSorted ? (
        <FaSort size={12} />
      ) : isSortedDesc ? (
        <FaSortDown size={12} />
      ) : (
        <FaSortUp size={12} />
      )}
    </Wrapper>
  );
};

export const ariaSort = (column: any) => {
  if (!column.canSort) return undefined;
  if (!column.isSorted) return 'none';
  return column.isSortedDesc ? 'descending' : 'ascending';
};

export default SortIndicator;
